import { useTranslation } from 'react-i18next';

import { formatCurrency } from '../../../helpers/utils';
import { DEFAULT_EMAIL_TEMPLATES } from '../defaults';
import type { EmailTemplateKey, LateFeePolicy } from '../types';

interface Props {
  policy: LateFeePolicy;
}

const EXAMPLE_INVOICE_AMOUNT = 500;

const substituteTokens = (text: string, values: Record<string, string>) =>
  text.replace(/\[([^\]]+)\]/g, (_match, token: string) => values[token] ?? `Example ${token.replace(/[-_]/g, ' ')}`);

export default function ReminderEmailPreview({ policy }: Props) {
  const { t } = useTranslation();
  const calculatedLateFee =
    policy.lateFeeType === 'percent'
      ? Math.min((EXAMPLE_INVOICE_AMOUNT * policy.lateFeeAmount) / 100, policy.maxLateFee)
      : policy.lateFeeAmount;
  const lateFeeAmount = formatCurrency(calculatedLateFee);
  const tokenValues = {
    parent_name: 'Alex Morgan',
    child_name: 'Jordan',
    invoice_amount: formatCurrency(EXAMPLE_INVOICE_AMOUNT),
    due_date: 'September 15, 2026',
    // Overridden per template below — a fixed value here would print
    // "7 days past due" inside the notice that sends at 14 days.
    days_past_due: '',
    late_fee_amount: lateFeeAmount,
    total_now_owed: formatCurrency(EXAMPLE_INVOICE_AMOUNT + calculatedLateFee),
    payment_link: 'pay.wonderschool.com/example (example only)',
    school_name: 'Little Oaks Child Care',
    director_name: 'Taylor Lee',
  };
  // Each rung quotes its own age. The past-due reminder fires while the family
  // is still inside the grace period, so it must read fewer days than the grace
  // period or it contradicts its own "we haven't applied a late fee yet".
  const gracePeriodDays = policy.gracePeriodDays;
  const daysPastDueFor = (key: EmailTemplateKey): number => {
    switch (key) {
      case 'reminder_past_due':
        return Math.max(1, gracePeriodDays - 1);
      case 'late_fee_applied':
        return gracePeriodDays;
      case 'firm_reminder':
        return policy.firmReminderDaysAfter;
      case 'escalation':
        return policy.escalationDaysAfter;
      default:
        return gracePeriodDays;
    }
  };

  const timing: Record<EmailTemplateKey, string> = {
    reminder_due_soon: t('billingSettings.reminderPreview.timing.dueSoon', {
      count: policy.reminderDaysBefore,
    }),
    reminder_due_today: t('billingSettings.reminderPreview.timing.dueToday'),
    reminder_past_due: t('billingSettings.reminderPreview.timing.pastDue'),
    late_fee_applied: t('billingSettings.reminderPreview.timing.lateFeeApplied'),
    firm_reminder: t('billingSettings.reminderPreview.timing.firm', {
      count: policy.firmReminderDaysAfter,
    }),
    escalation: t('billingSettings.reminderPreview.timing.escalation', {
      count: policy.escalationDaysAfter,
    }),
  };
  return (
    <section
      aria-labelledby="reminder-email-preview-heading"
      className="rounded-lg border bg-white p-5"
      data-testid="reminder-email-preview"
    >
      <h2 id="reminder-email-preview-heading" className="text-lg font-semibold text-gray-900">
        {t('billingSettings.reminderPreview.heading')}
      </h2>
      <p className="mt-1 text-sm text-gray-600">{t('billingSettings.reminderPreview.description')}</p>
      <p className="mt-3 rounded-md bg-blue-50 px-3 py-2 text-sm text-blue-950">
        {t(
          policy.policyEnabled
            ? 'billingSettings.reminderPreview.sending'
            : 'billingSettings.reminderPreview.notSending'
        )}
      </p>
      <p className="mt-3 text-sm text-gray-600">{t('billingSettings.reminderPreview.exampleNote')}</p>
      <p className="mt-1 text-sm text-gray-600">{t('billingSettings.reminderPreview.englishOnly')}</p>

      <div className="mt-4 divide-y divide-gray-200 border-y border-gray-200">
        {DEFAULT_EMAIL_TEMPLATES.map((template) => {
          // [days_past_due] has to match the rung that is sending, or the final
          // notice reads "7 days past due" under a "14 days after" heading.
          const tokens = { ...tokenValues, days_past_due: String(daysPastDueFor(template.key)) };
          const subject = substituteTokens(template.subject, tokens);
          const body = substituteTokens(template.body, tokens);

          return (
            <details key={template.key} className="py-1">
              <summary className="cursor-pointer py-3 text-left text-blue-900 hover:text-blue-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-800">
                <span className="ml-2 inline-block min-w-0 align-top">
                  <span className="block text-sm font-semibold text-gray-900">{timing[template.key]}</span>
                  <span className="mt-1 block text-sm font-normal text-gray-600">{subject}</span>
                </span>
              </summary>
              <div className="border-t border-gray-200 px-1 py-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {t('billingSettings.reminderPreview.subjectLabel')}
                </p>
                <p className="mt-1 text-sm font-medium text-gray-900">{subject}</p>
                <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {t('billingSettings.reminderPreview.bodyLabel')}
                </p>
                <p
                  className="mt-1 whitespace-pre-wrap text-sm leading-6 text-gray-800"
                  data-testid={`reminder-email-body-${template.key}`}
                >
                  {body}
                </p>
              </div>
            </details>
          );
        })}
      </div>
    </section>
  );
}
